import { useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * react-router v5 `useHistory()` compatibility shim for the v7 upgrade.
 *
 * react-router-dom v6/v7 removed `useHistory`. Rather than rewrite the ~377
 * `history.push/replace/goBack` call-sites (many inside the fragile subscription
 * flow), this hook reproduces the v5 `history` object on top of v7's
 * `useNavigate`/`useLocation`, so every existing call-site keeps working
 * byte-for-byte. Only the import source changes.
 *
 * - Stable object identity (memoized on the stable `navigate`) so effects with
 *   `[history]` in their deps don't re-run every render.
 * - Fresh `.location` via a ref-backed getter (the memoized object would
 *   otherwise capture a stale location).
 * - `push`/`replace` accept all v5 forms: `(string)`, `(string, state)`, and a
 *   location object `({ pathname, search, hash, state })`. v5 `<Redirect>` /
 *   `history.replace` default to REPLACE, matched here.
 * - `listen`/`block` are intentionally NOT implemented. The only `history.listen`
 *   site (form-management scroll-restore) is rewritten to `useLocation` +
 *   `useNavigationType`. A stray `.listen()`/`.block()` will throw loudly rather
 *   than silently no-op, surfacing any missed migration.
 */
export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  return useMemo(() => {
    const toArgs = (to, state) => {
      if (typeof to === "string") return [to, { state }];
      if (to && typeof to === "object") {
        const { state: locState, ...path } = to;
        return [path, { state: state !== undefined ? state : locState }];
      }
      return [to, { state }];
    };
    return {
      push: (to, state) => {
        const [t, opts] = toArgs(to, state);
        navigate(t, opts);
      },
      replace: (to, state) => {
        const [t, opts] = toArgs(to, state);
        navigate(t, { ...opts, replace: true });
      },
      go: (delta) => navigate(delta),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      get location() {
        return locationRef.current;
      },
    };
  }, [navigate]);
}

export default useHistory;
